import { productUnits } from '@eneco/enumerations/productUnits';

import type {
  OxxioElectricityRedeliverySection,
  OxxioElectricityVariableSection,
  OxxioFixedCostsSection,
  OxxioGasVariableSection,
  OxxioGovernmentLeviesSection,
  OxxioRedeliveryBlock,
  OxxioSectionsBuilderContext,
  OxxioTariffRow,
} from './oxxio-types';
import { getDisplayYear } from '../../../../eneco/components/PriceDetailsOverview/price-details-overview.helpers';
import { checkOfferIncludesDynamicPricingProduct } from '../../../../shared/helpers';

const getDeliveryCostsComponentPrice = (
  tariff:
    | {
        components?: Array<{ type: string } & Partial<Record<'vatIncluded' | 'vatExcluded', number>>>;
      }
    | undefined,
  costsType: 'vatIncluded' | 'vatExcluded',
): number => tariff?.components?.find(component => component.type === 'deliverycosts')?.[costsType] ?? 0;

const buildPurchaseFeeRows = ({
  marketPrice,
  marketTotal,
  purchaseFeePrice,
  purchaseFeeTotal,
  usage,
}: {
  marketPrice: number;
  marketTotal: number;
  purchaseFeePrice: number;
  purchaseFeeTotal: number;
  usage: number;
}): OxxioElectricityVariableSection['purchaseFee'] => ({
  marketPrice: {
    label: 'Geschatte marktprijs',
    tariff: marketPrice,
    usage,
    total: marketTotal,
  },
  purchaseFee: {
    label: 'Inkoopvergoeding',
    tariff: purchaseFeePrice,
    usage,
    total: purchaseFeeTotal,
  },
});

export const buildOxxioElectricityVariableSection = (
  context: OxxioSectionsBuilderContext,
): OxxioElectricityVariableSection | null => {
  const { costDetails, costsType, textualData, hasDoubleMeter, offer, estimatedElectricityHigh } = context;

  const headerLabel = `${textualData?.variableCostsLabels?.variableCostsLabel?.value} ${textualData?.productTypeLabels?.electricityProductLabel?.value}`;
  const headerValue = costDetails?.electricityTotal?.yearlyCostsVariable?.[costsType] ?? 0;

  const hasElectricityPurchaseFee =
    hasDoubleMeter &&
    checkOfferIncludesDynamicPricingProduct(offer) &&
    Number.isFinite(costDetails?.electricityTotal?.purchaseFee?.[costsType]) &&
    Number(costDetails?.electricityTotal?.purchaseFee?.[costsType]) > 0;

  if (hasElectricityPurchaseFee) {
    return {
      header: { label: headerLabel, value: headerValue },
      purchaseFee: buildPurchaseFeeRows({
        marketPrice: costDetails?.electricityCostDetails?.tariffWithoutTax?.price?.[costsType] ?? 0,
        marketTotal: costDetails?.electricityTotal?.yearlyCostsVariableWithoutTax?.[costsType] ?? 0,
        purchaseFeePrice: costDetails?.electricityCostDetails?.purchaseFee?.price?.[costsType] ?? 0,
        purchaseFeeTotal: costDetails?.electricityTotal?.purchaseFee?.[costsType] ?? 0,
        usage: costDetails?.electricityTotal?.purchaseFee?.usage ?? 0,
      }),
    };
  }

  const high: OxxioTariffRow = {
    label: textualData?.variableCostsLabels?.electricityHighLabel?.value ?? '',
    tariff: getDeliveryCostsComponentPrice(costDetails?.electricityCostDetails?.tariffWithoutTax, costsType),
    usage: estimatedElectricityHigh ?? 0,
    total:
      costDetails?.electricityTotal?.[
        hasDoubleMeter ? 'yearlyCostsVariableHighWithoutTax' : 'yearlyCostsVariableWithoutTax'
      ]?.[costsType] ?? 0,
    icon: 'electricity',
  };

  const low: OxxioTariffRow | undefined = hasDoubleMeter
    ? {
        label: textualData?.variableCostsLabels?.electricityLowLabel?.value ?? '',
        tariff: getDeliveryCostsComponentPrice(costDetails?.electricityCostDetails?.lowTariffWithoutTax, costsType),
        usage: context.estimatedElectricityLow ?? 0,
        total: costDetails?.electricityTotal?.yearlyCostsVariableLowWithoutTax?.[costsType] ?? 0,
        icon: 'electricity',
      }
    : undefined;

  return {
    header: { label: headerLabel, value: headerValue },
    standardTariff: { high, low },
  };
};

export const buildOxxioElectricityRedeliverySection = (
  context: OxxioSectionsBuilderContext,
  popoverContent: string,
): OxxioElectricityRedeliverySection | null => {
  const {
    costDetails,
    costsType,
    textualData,
    offer,
    hasSolarPanels,
    solarPanelsOutput,
    electricityRedeliveryRemainder,
  } = context;

  if (!hasSolarPanels) {
    return null;
  }

  const blocks: OxxioRedeliveryBlock[] = [
    {
      type: 'header',
      total: costDetails?.electricityTotal?.redeliveryCostTotal?.[costsType] ?? 0,
      showPopover:
        !!textualData?.costDetails?.redeliveryTariffPopoverDescription?.value && !context.isDynamicOrVariableOffer,
      popoverTitle: textualData?.costDetails?.redeliveryTariffPopoverDescription?.value,
      popoverContent,
    },
  ];

  if (!solarPanelsOutput) {
    return { blocks };
  }

  const currentYear = getDisplayYear(offer, offer?.discountType);
  const redeliveryCostTariff = costDetails?.electricityCostDetails?.redeliveryCostTariff?.price?.[costsType];
  const redeliveryUsage =
    (solarPanelsOutput ?? 0) - (costDetails?.electricityFutureCostDetails?.electricityRedeliveryRefund?.usage ?? 0);

  if (redeliveryCostTariff) {
    blocks.push(
      { type: 'yearTitle', year: currentYear },
      {
        type: 'tariff',
        label: `Terugleverkosten ${currentYear}`,
        tariff: redeliveryCostTariff,
        usage: redeliveryUsage,
        total: costDetails?.electricityTotal?.redeliveryCost?.[costsType] ?? 0,
      },
    );
  }

  if (electricityRedeliveryRemainder > 0) {
    blocks.push({
      type: 'tariff',
      label: `Terugleververgoeding ${currentYear}`,
      tariff: costDetails?.electricityCostDetails?.redeliverytariff?.price?.vatExcluded ?? 0,
      usage: electricityRedeliveryRemainder,
      total: costDetails?.electricityTotal?.electricityRedeliveryRefund?.vatExcluded ?? 0,
      showDividerBefore: !redeliveryCostTariff,
    });
  }

  const futureRate = offer?.productFutureRate;
  const futureRedeliveryUsage = costDetails?.electricityFutureCostDetails?.redeliveryCost?.usage ?? 0;

  if (futureRate?.zonOpDak && futureRedeliveryUsage > 0) {
    blocks.push(
      { type: 'yearTitle', year: '2027' },
      {
        type: 'tariff',
        label: 'Terugleverkosten 2027',
        tariff: futureRate.zonOpDak[costsType] ?? 0,
        usage: futureRedeliveryUsage,
        total: costDetails?.electricityFutureCostDetails?.redeliveryCost?.price?.[costsType] ?? 0,
      },
    );
  }

  const futureRefundUsage = costDetails?.electricityFutureCostDetails?.electricityRedeliveryRefund?.usage ?? 0;

  if (futureRefundUsage > 0 && futureRate?.electricityRedeliveryRefund) {
    blocks.push({
      type: 'tariff',
      label: 'Terugleververgoeding 2027',
      tariff: futureRate.electricityRedeliveryRefund.vatExcluded ?? 0,
      usage: futureRefundUsage,
      total: costDetails?.electricityFutureCostDetails?.electricityRedeliveryRefund?.price?.vatExcluded ?? 0,
    });
  }

  if ((costDetails?.electricityTotal?.salesFee?.vatIncluded ?? 0) > 0) {
    blocks.push({
      type: 'salesFee',
      label: 'Verkoopvergoeding',
      tariff: costDetails?.electricityCostDetails?.salesFee?.price?.[costsType] ?? 0,
      usage: costDetails?.electricityTotal?.salesFee?.usage ?? 0,
      total: costDetails?.electricityTotal?.salesFee?.[costsType] ?? 0,
    });
  }

  return { blocks };
};

export const buildOxxioGasVariableSection = (context: OxxioSectionsBuilderContext): OxxioGasVariableSection | null => {
  const { costDetails, costsType, textualData, gas } = context;

  const headerLabel = `${textualData?.variableCostsLabels?.variableCostsLabel?.value} ${textualData?.productTypeLabels?.gasProductLabel?.value}`;
  const headerValue = costDetails?.gasTotal?.yearlyCostsVariableWithoutTax?.[costsType] ?? 0;

  const hasGasPurchaseFee =
    Number.isFinite(costDetails?.gasTotal?.purchaseFee?.[costsType]) &&
    Number(costDetails?.gasTotal?.purchaseFee?.[costsType]) > 0;

  if (hasGasPurchaseFee) {
    return {
      header: { label: headerLabel, value: headerValue },
      purchaseFee: buildPurchaseFeeRows({
        marketPrice: costDetails?.gasCostDetails?.tariffWithoutTax?.price?.[costsType] ?? 0,
        marketTotal: costDetails?.gasTotal?.yearlyCostsVariableWithoutTax?.[costsType] ?? 0,
        purchaseFeePrice: costDetails?.gasCostDetails?.purchaseFee?.price?.[costsType] ?? 0,
        purchaseFeeTotal: costDetails?.gasTotal?.purchaseFee?.[costsType] ?? 0,
        usage: costDetails?.gasTotal?.purchaseFee?.usage ?? 0,
      }),
    };
  }

  return {
    header: { label: headerLabel, value: headerValue },
    standardTariff: {
      label: textualData?.variableCostsLabels?.gasLabel?.value ?? '',
      tariff: getDeliveryCostsComponentPrice(costDetails?.gasCostDetails?.tariffWithoutTax, costsType),
      usage: gas,
      total: costDetails?.gasTotal?.yearlyCostsVariableWithoutTax?.[costsType] ?? 0,
      icon: 'gas',
    },
  };
};

export const buildOxxioElectricityGovernmentLeviesSection = (
  context: OxxioSectionsBuilderContext,
): OxxioGovernmentLeviesSection => {
  const { costDetails, costsType, textualData, electricityUsageTotal, formatter } = context;
  const { number } = formatter;

  return {
    header: {
      label: `${textualData?.costDetails?.governmentLeviesLabel?.value} ${textualData?.productTypeLabels?.electricityProductLabel?.value}`,
      value: costDetails?.electricityTotal?.yearlyEnergyTax?.[costsType] ?? 0,
    },
    productLabel: textualData?.productTypeLabels?.electricityProductLabel?.value ?? '',
    icon: 'electricity',
    brackets: Object.keys(costDetails?.electricityEnergyTaxDetails ?? {})
      .map(key => costDetails?.electricityEnergyTaxDetails?.[key])
      .filter(bracket => bracket?.active)
      .map(bracket => ({
        usageRange: `${number.noFractionDigits(bracket?.usageMin ?? 0)} - ${number.noFractionDigits(bracket?.usageMax ?? 0)} ${textualData?.costDetails?.electricityTaxBracket1Label?.value ?? ''}`,
        price: bracket?.price?.[costsType] ?? 0,
        unit: productUnits.electricity,
      })),
    usageLabel: textualData?.costDetails?.usageLabel?.value ?? '',
    usageTotal: electricityUsageTotal,
    usageUnit: 'electricity',
    totalTax: costDetails?.electricityTotal?.yearlyEnergyTax?.[costsType] ?? 0,
  };
};

export const buildOxxioGasGovernmentLeviesSection = (
  context: OxxioSectionsBuilderContext,
): OxxioGovernmentLeviesSection => {
  const { costDetails, costsType, textualData, gasUsageTotal, formatter } = context;
  const { number } = formatter;

  return {
    header: {
      label: `${textualData?.costDetails?.governmentLeviesLabel?.value} ${textualData?.productTypeLabels?.gasProductLabel?.value}`,
      value: costDetails?.gasTotal?.yearlyEnergyTax?.[costsType] ?? 0,
    },
    productLabel: textualData?.variableCostsLabels?.gasLabel?.value ?? '',
    icon: 'gas',
    brackets: Object.keys(costDetails?.gasEnergyTaxDetails ?? {})
      .map(key => costDetails?.gasEnergyTaxDetails?.[key])
      .filter(bracket => bracket?.active)
      .map(bracket => ({
        usageRange: `${number.noFractionDigits(bracket?.usageMin ?? 0)} - ${number.noFractionDigits(bracket?.usageMax ?? 0)} ${textualData?.costDetails?.gasTaxBracket1Label?.value ?? ''}`,
        price: bracket?.price?.[costsType] ?? 0,
        unit: productUnits.gas,
      })),
    usageLabel: textualData?.costDetails?.usageLabel?.value ?? '',
    usageTotal: gasUsageTotal,
    usageUnit: 'gas',
    totalTax: costDetails?.gasTotal?.yearlyEnergyTax?.[costsType] ?? 0,
  };
};

export const buildOxxioElectricityFixedCostsSection = (
  context: OxxioSectionsBuilderContext,
): OxxioFixedCostsSection => {
  const { costDetails, costsType, textualData } = context;

  return {
    header: {
      label: `${textualData?.fixedCostsLabels?.fixedCostsLabel?.value} ${textualData?.productTypeLabels?.electricityProductLabel?.value}`,
      value: costDetails?.electricityTotal?.yearlyCostsFixed?.[costsType] ?? 0,
    },
    lines: [
      {
        label: textualData?.fixedCostsLabels?.standardChargeLabel?.value ?? '',
        value: costDetails?.electricityTotal?.standingCharge?.[costsType] ?? 0,
      },
      {
        label:
          textualData?.fixedCostsLabels?.deliveryCostsLabel?.value?.replace(
            '{value}',
            costDetails?.electricityCostDetails?.deliverycosts?.gridOperatorName ?? '',
          ) ?? '',
        value: costDetails?.electricityCostDetails?.deliverycosts?.price?.[costsType] ?? 0,
      },
      {
        label: textualData?.fixedCostsLabels?.energyTaxReductionLabel?.value ?? '',
        value: costDetails?.electricityCostDetails?.taxdiscount?.price?.[costsType] ?? 0,
      },
    ],
  };
};

export const buildOxxioGasFixedCostsSection = (context: OxxioSectionsBuilderContext): OxxioFixedCostsSection => {
  const { costDetails, costsType, textualData } = context;

  return {
    header: {
      label: `${textualData?.fixedCostsLabels?.fixedCostsLabel?.value} ${textualData?.productTypeLabels?.gasProductLabel?.value}`,
      value: costDetails?.gasTotal?.yearlyCostsFixed?.[costsType] ?? 0,
    },
    lines: [
      {
        label: textualData?.fixedCostsLabels?.standardChargeLabel?.value ?? '',
        value: costDetails?.gasTotal?.standingCharge?.[costsType] ?? 0,
      },
      {
        label:
          textualData?.fixedCostsLabels?.deliveryCostsLabel?.value?.replace(
            '{value}',
            costDetails?.gasCostDetails?.deliverycosts?.gridOperatorName ?? '',
          ) ?? '',
        value: costDetails?.gasCostDetails?.deliverycosts?.price?.[costsType] ?? 0,
      },
    ],
  };
};
